/**
 * Tenant Sync history — paged jobs list, then one batch's sync_decision
 * journal (WSRPDM-0073). URL is the source of truth: a valid `?job=` shows
 * the journal; otherwise the list. No query client; fetch on filter/page/
 * reload only (Notifications pattern).
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { FilterTabs, SeverityPill, StatusPill } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { listDecisions, listJobs } from '@/api/wsr/jobs';
import { wsrApiError } from '@/api/wsr/errors';
import type {
  IngestionJobView,
  QueueJobStatus,
  SyncDecisionFacts,
  SyncDecisionView,
} from '@/api/wsr/types';
import { jobTypeLabel, statusLabel, severityLabel } from '@/domain/registry';
import { formatSecondsDuration } from '../../queue/duration';
import { ActivityCountersLine, activityKindValue } from '../activityFormat';

const PAGE_SIZE = 20;
const HEADER_LOOKUP_SIZE = 100;
const JOB_ID_RE = /^[1-9][0-9]*$/;

const QUEUE_STATUSES: QueueJobStatus[] = [
  'PENDING',
  'CLAIMED',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'SKIPPED',
];

const DISPATCHABLE_TYPES = [
  'USER_SYNC',
  'SALES_SYNC',
  'ATTENDANCE_SYNC',
  'PRODUCT_SYNC',
  'SALES_RECONCILE',
  'REPORT_USER_SYNC_SUMMARY',
] as const;

const KNOWN_FACT_KEYS = new Set(['fields', 'tagsAdded', 'tagsRemoved', 'reason']);

type StatusTab = 'ALL' | QueueJobStatus;
type TypeFilter = 'ALL' | (typeof DISPATCHABLE_TYPES)[number];
type SeverityFilter = 'ALL' | 'INFO' | 'WARN' | 'ERROR';
type JobCache = MutableRefObject<Map<number, IngestionJobView>>;
type ListRef = MutableRefObject<IngestionJobView[]>;

export function parseJobId(raw: string | null): number | null {
  if (raw == null || !JOB_ID_RE.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
}

function finishedAgo(finishedAt: string | null, t: ReturnType<typeof useCopy>['t']): string {
  if (finishedAt == null) return '—';
  const elapsedSeconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(finishedAt).getTime()) / 1000),
  );
  return t('activity.ranAgo', '{{duration}} ago', {
    duration: formatSecondsDuration(elapsedSeconds),
  });
}

function emptyJournalCopy(
  jobType: string | undefined,
  t: ReturnType<typeof useCopy>['t'],
): string {
  if (jobType === 'USER_SYNC') {
    return t('syncHistory.emptyJournalUserSync', 'No per-user changes on this run.');
  }
  if (jobType) {
    return t(
      'syncHistory.emptyJournalOtherType',
      'This job type has no per-row journal. Outcome and counts are on the run header.',
    );
  }
  return t('syncHistory.emptyJournalUnknown', 'No per-row journal for this run.');
}

function opsNotProvisionedCopy(t: ReturnType<typeof useCopy>['t']): string {
  return t(
    'tenantDetail.identity.opsNotProvisioned',
    "Operational settings for this tenant aren't available yet — wsr-api hasn't mirrored it. It will appear on its next roster sync.",
  );
}

function Pager({
  page,
  totalPages,
  loading,
  onPageChange,
  prevLabel,
  nextLabel,
  indicator,
}: {
  page: number;
  totalPages: number;
  loading: boolean;
  onPageChange: (next: number) => void;
  prevLabel: string;
  nextLabel: string;
  indicator: string;
}) {
  if (totalPages <= 1) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
      <Button
        size="small"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 0 || loading}
        aria-label={prevLabel}
      >
        {prevLabel}
      </Button>
      <Typography variant="caption" color="text.secondary">
        {indicator}
      </Typography>
      <Button
        size="small"
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages - 1 || loading}
        aria-label={nextLabel}
      >
        {nextLabel}
      </Button>
    </Box>
  );
}

export function SyncHistoryPanel({ slug }: { slug: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = parseJobId(searchParams.get('job'));
  const jobCache = useRef(new Map<number, IngestionJobView>());
  const listContentRef = useRef<IngestionJobView[]>([]);

  if (jobId !== null) {
    return (
      <JournalView
        key={jobId}
        slug={slug}
        jobId={jobId}
        jobCache={jobCache}
        listContentRef={listContentRef}
        onBack={() => setSearchParams({ tab: 'history' })}
      />
    );
  }

  return (
    <JobsListView
      slug={slug}
      jobCache={jobCache}
      listContentRef={listContentRef}
      onOpenJob={(job) => {
        jobCache.current.set(job.id, job);
        setSearchParams({ tab: 'history', job: String(job.id) });
      }}
    />
  );
}

function JobsListView({
  slug,
  jobCache,
  listContentRef,
  onOpenJob,
}: {
  slug: string;
  jobCache: JobCache;
  listContentRef: ListRef;
  onOpenJob: (job: IngestionJobView) => void;
}) {
  const { t } = useCopy();
  const [statusTab, setStatusTab] = useState<StatusTab>('ALL');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  const [page, setPage] = useState(0);
  const [content, setContent] = useState<IngestionJobView[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadMessage, setLoadMessage] = useState<string | null>(null);
  const [unknownTenant, setUnknownTenant] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listJobs(slug, {
      status: statusTab === 'ALL' ? undefined : statusTab,
      type: typeFilter === 'ALL' ? undefined : typeFilter,
      page,
      size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setContent(res.content);
        listContentRef.current = res.content;
        for (const job of res.content) jobCache.current.set(job.id, job);
        setTotalPages(res.totalPages);
        setLoadFailed(false);
        setLoadMessage(null);
        setUnknownTenant(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const wsrErr = wsrApiError(err);
        if (wsrErr?.code === 'UNKNOWN_TENANT') {
          setUnknownTenant(true);
          setLoadFailed(false);
          setLoadMessage(null);
          return;
        }
        setUnknownTenant(false);
        setLoadFailed(true);
        setLoadMessage(wsrErr?.message ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, statusTab, typeFilter, page, reloadToken, jobCache, listContentRef]);

  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };
  const handleStatusTabChange = (key: string) => {
    setLoading(true);
    setStatusTab(key as StatusTab);
    setPage(0);
  };
  const handleTypeChange = (value: string) => {
    setLoading(true);
    setTypeFilter(value as TypeFilter);
    setPage(0);
  };
  const handlePageChange = (next: number) => {
    setLoading(true);
    setPage(next);
  };

  const tabs = [
    { key: 'ALL', label: t('syncHistory.filters.allStatuses', 'All statuses') },
    ...QUEUE_STATUSES.map((status) => ({
      key: status,
      label: statusLabel(status, t),
    })),
  ];

  if (unknownTenant) {
    return (
      <Box>
        <Typography variant="h2" component="h2" gutterBottom>
          {t('syncHistory.title', 'Sync history')}
        </Typography>
        <Alert severity="info">{opsNotProvisionedCopy(t)}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h2" component="h2" gutterBottom>
        {t('syncHistory.title', 'Sync history')}
      </Typography>

      {loadFailed ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Typography
              component="button"
              variant="caption"
              onClick={refetch}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {t('common.retry', 'Retry')}
            </Typography>
          }
        >
          {loadMessage ?? t('syncHistory.loadError', 'Could not load sync history.')}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <TextField
          select
          size="small"
          label={t('syncHistory.filters.typeLabel', 'Job type')}
          value={typeFilter}
          onChange={(e) => handleTypeChange(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="ALL">{t('syncHistory.filters.allTypes', 'All types')}</MenuItem>
          {DISPATCHABLE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {jobTypeLabel(type, t)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <FilterTabs
          tabs={tabs}
          active={statusTab}
          onChange={handleStatusTabChange}
          ariaLabel={t('syncHistory.filters.statusAria', 'Filter by status')}
        />
      </Box>

      {loading ? <LoadingSpinner /> : null}

      {!loading && !loadFailed ? (
        content.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('syncHistory.emptyJobs', 'No sync runs in the last 14 days.')}
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={t('syncHistory.title', 'Sync history')}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('syncHistory.col.id', 'Id')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('syncHistory.col.job', 'Job')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.status', 'Status')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('syncHistory.col.kind', 'Kind')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.finished', 'Finished')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.records', 'Records')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{job.id}</TableCell>
                    <TableCell>
                      <Button variant="text" size="small" onClick={() => onOpenJob(job)}>
                        {jobTypeLabel(job.jobType, t)}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={job.status} />
                    </TableCell>
                    <TableCell>{activityKindValue(job.runKind, t)}</TableCell>
                    <TableCell>{finishedAgo(job.finishedAt, t)}</TableCell>
                    <TableCell>
                      {job.runCounters == null ? (
                        '—'
                      ) : (
                        <ActivityCountersLine counters={job.runCounters} includeUnknownKeys />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}

      <Pager
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPageChange={handlePageChange}
        prevLabel={t('syncHistory.prevPage', 'Previous page')}
        nextLabel={t('syncHistory.nextPage', 'Next page')}
        indicator={t('syncHistory.pageIndicator', 'Page {{page}} of {{total}}', {
          page: page + 1,
          total: totalPages,
        })}
      />
    </Box>
  );
}

function JournalView({
  slug,
  jobId,
  jobCache,
  listContentRef,
  onBack,
}: {
  slug: string;
  jobId: number;
  jobCache: JobCache;
  listContentRef: ListRef;
  onBack: () => void;
}) {
  const { t } = useCopy();
  const [header, setHeader] = useState<IngestionJobView | null>(null);
  const [headerPending, setHeaderPending] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('ALL');
  const [page, setPage] = useState(0);
  const [content, setContent] = useState<SyncDecisionView[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadMessage, setLoadMessage] = useState<string | null>(null);
  const [unknownTenant, setUnknownTenant] = useState(false);
  const [jobGone, setJobGone] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const cached =
      jobCache.current.get(jobId) ?? listContentRef.current.find((job) => job.id === jobId);
    if (cached) {
      jobCache.current.set(jobId, cached);
      setHeader(cached);
      setHeaderPending(false);
      return;
    }
    let cancelled = false;
    listJobs(slug, { page: 0, size: HEADER_LOOKUP_SIZE })
      .then((res) => {
        if (cancelled) return;
        const found = res.content.find((job) => job.id === jobId);
        if (found) jobCache.current.set(jobId, found);
        setHeader(found ?? null);
        setHeaderPending(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHeader(null);
          setHeaderPending(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobCache, jobId, listContentRef, slug]);

  useEffect(() => {
    if (jobGone) return;
    let cancelled = false;
    listDecisions(slug, jobId, {
      severity: severityFilter === 'ALL' ? undefined : severityFilter,
      page,
      size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setContent(res.content);
        setTotalPages(res.totalPages);
        setLoadFailed(false);
        setLoadMessage(null);
        setUnknownTenant(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const wsrErr = wsrApiError(err);
        if (wsrErr?.code === 'UNKNOWN_TENANT') {
          setUnknownTenant(true);
          setLoadFailed(false);
          setJobGone(false);
          return;
        }
        if (wsrErr?.code === 'JOB_NOT_FOUND') {
          setJobGone(true);
          setLoadFailed(false);
          setUnknownTenant(false);
          return;
        }
        setLoadFailed(true);
        setLoadMessage(wsrErr?.message ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, jobId, severityFilter, page, reloadToken, jobGone]);

  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };
  const handleSeverityChange = (value: string) => {
    setLoading(true);
    setSeverityFilter(value as SeverityFilter);
    setPage(0);
  };
  const handlePageChange = (next: number) => {
    setLoading(true);
    setPage(next);
  };

  const resolvedType = headerPending
    ? undefined
    : (header?.jobType ?? content[0]?.jobType);
  const journalPending = loading || headerPending;
  const backControl = (
    <Button variant="text" onClick={onBack}>
      {t('syncHistory.backToList', 'Back to list')}
    </Button>
  );

  if (unknownTenant) {
    return (
      <Box>
        {backControl}
        <Alert severity="info" sx={{ mt: 2 }}>
          {opsNotProvisionedCopy(t)}
        </Alert>
      </Box>
    );
  }

  if (jobGone) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('syncHistory.jobNotFound', 'This run is no longer retained.')}
        </Alert>
        {backControl}
      </Box>
    );
  }

  return (
    <Box>
      {backControl}
      <Typography variant="h2" component="h2" sx={{ mt: 1 }} gutterBottom>
        {t('syncHistory.jobTitle', 'Job {{id}}', { id: jobId })}
      </Typography>

      {!headerPending ? (
        header ? (
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="body1">{jobTypeLabel(header.jobType, t)}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <StatusPill status={header.status} />
              <Typography variant="body2">{activityKindValue(header.runKind, t)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {finishedAgo(header.finishedAt, t)}
              </Typography>
            </Stack>
            {header.runCounters == null ? (
              <Typography variant="body2">—</Typography>
            ) : (
              <ActivityCountersLine counters={header.runCounters} includeUnknownKeys />
            )}
            {header.errorDetail ? (
              <Box>
                <Typography variant="body2">{header.errorDetail}</Typography>
                {header.errorCode ? (
                  <Typography variant="caption" color="text.secondary">
                    {header.errorCode}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {resolvedType ? (
              <Typography variant="body1">{jobTypeLabel(resolvedType, t)}</Typography>
            ) : null}
            <Typography variant="body2">—</Typography>
          </Stack>
        )
      ) : null}

      <Typography variant="h3" component="h3" sx={{ mb: 2 }}>
        {t('syncHistory.journalTitle', 'Journal')}
      </Typography>

      {loadFailed ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Typography
              component="button"
              variant="caption"
              onClick={refetch}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {t('common.retry', 'Retry')}
            </Typography>
          }
        >
          {loadMessage ?? t('syncHistory.loadError', 'Could not load sync history.')}
        </Alert>
      ) : null}

      <TextField
        select
        size="small"
        label={t('syncHistory.filters.severityLabel', 'Severity')}
        value={severityFilter}
        onChange={(e) => handleSeverityChange(e.target.value)}
        sx={{ minWidth: 180, mb: 2 }}
      >
        <MenuItem value="ALL">{t('syncHistory.filters.allSeverities', 'All severities')}</MenuItem>
        <MenuItem value="INFO">{severityLabel('INFO', t)}</MenuItem>
        <MenuItem value="WARN">{severityLabel('WARN', t)}</MenuItem>
        <MenuItem value="ERROR">{severityLabel('ERROR', t)}</MenuItem>
      </TextField>

      {journalPending ? <LoadingSpinner /> : null}

      {!journalPending && !loadFailed ? (
        content.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {emptyJournalCopy(resolvedType, t)}
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={t('syncHistory.journalTitle', 'Journal')}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('syncHistory.col.when', 'When')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.severity', 'Sev')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.action', 'Action')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.entity', 'Entity')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t('syncHistory.col.summary', 'Summary')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.map((row) => (
                  <DecisionRow key={row.id} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}

      <Pager
        page={page}
        totalPages={totalPages}
        loading={loading}
        onPageChange={handlePageChange}
        prevLabel={t('syncHistory.prevPage', 'Previous page')}
        nextLabel={t('syncHistory.nextPage', 'Next page')}
        indicator={t('syncHistory.pageIndicator', 'Page {{page}} of {{total}}', {
          page: page + 1,
          total: totalPages,
        })}
      />
    </Box>
  );
}

function DecisionRow({ row }: { row: SyncDecisionView }) {
  const { t } = useCopy();
  const [expanded, setExpanded] = useState(false);
  const facts = row.facts;
  const hasFacts = facts != null && typeof facts === 'object';
  const when = new Date(row.occurredAt).toLocaleString();

  return (
    <TableRow>
      <TableCell>{when}</TableCell>
      <TableCell>
        <SeverityPill severity={row.severity} />
      </TableCell>
      <TableCell>{t(`syncHistory.action.${row.action}`, row.action)}</TableCell>
      <TableCell>
        {t(`syncHistory.entity.${row.entityKind}`, row.entityKind)} {row.entityKey}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.summary}</Typography>
        {hasFacts ? (
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="small"
              variant="text"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? t('syncHistory.facts.hide', 'Hide details')
                : t('syncHistory.facts.show', 'Show details')}
            </Button>
            {expanded ? <FactsDetail facts={facts} /> : null}
          </Box>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function FactsDetail({ facts }: { facts: SyncDecisionFacts | Record<string, unknown> }) {
  const typed = facts as {
    fields?: Array<{ name: string; from: string; to: string }>;
    tagsAdded?: string[];
    tagsRemoved?: string[];
    reason?: string;
  };

  return (
    <Box sx={{ mt: 1 }}>
      {typed.fields?.map((field) => (
        <Typography key={field.name} variant="body2">
          {field.name}: {field.from} → {field.to}
        </Typography>
      ))}
      {typed.tagsAdded && typed.tagsAdded.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ my: 0.5, flexWrap: 'wrap' }}>
          {typed.tagsAdded.map((tag) => (
            <Chip key={`add-${tag}`} label={tag} size="small" color="success" variant="outlined" />
          ))}
        </Stack>
      ) : null}
      {typed.tagsRemoved && typed.tagsRemoved.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ my: 0.5, flexWrap: 'wrap' }}>
          {typed.tagsRemoved.map((tag) => (
            <Chip key={`rm-${tag}`} label={tag} size="small" color="default" variant="outlined" />
          ))}
        </Stack>
      ) : null}
      {typed.reason ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {typed.reason}
        </Typography>
      ) : null}
      {Object.keys(facts)
        .filter((key) => !KNOWN_FACT_KEYS.has(key))
        .map((key) => {
          const value = (facts as Record<string, unknown>)[key];
          if (value == null || (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')) {
            return null;
          }
          return (
            <Typography key={key} variant="body2">
              {key}: {String(value)}
            </Typography>
          );
        })}
    </Box>
  );
}
